import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useFeatureFlags } from '@fs/zion-flags'
import ZionForm, { useFieldArray, useZionForm, useWatch, useFormState } from '@fs/zion-form'
import { Alert, Cell, FlowGrid, H6, Paragraph } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  getPersonName,
  getSlsTypeByValue,
} from '@fs/zion-sls'
import { getFieldIdsFromFormValues } from './utils/events.utils'
import CardFooter from '../shared/components/CardFooter'
import SuggestionButtons from '../shared/components/SuggestionButtons'
import getEventsDispatches from './utils/saveEvents'
import { headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import { eventsResolver } from './utils/validationSchema'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useDisplayHighlights } from '../../../hooks/useDisplayHighlights'
import { createEmptyEventWithType, parseEventsForForm } from './utils/eventsParsing.utils'
import EventsSection from './components/EventsSection'

// Event types to show as suggestion buttons
const SUGGESTED_EVENT_TYPES = ['BIRTH', 'MARRIAGE', 'DEATH', 'RESIDENCE']

/**
 * EventsCardEditMode
 *
 * Edit mode UI for a person's events. Renders a ZionForm with sections for
 * primary and additional events, manages form state, and invokes onSave when
 * the form is submitted.
 *
 * Props:
 * @param {Object} props.person - The SLS person record. Expected to include a `primary` flag and other person-specific data.
 * @param {boolean} [props.reviewed=false] - Whether the events have been reviewed. Used to toggle review state when saving.
 * @param {Function} props.setIsEditing - Function to exit edit mode (typically sets editMode state to false).
 * @param {Array<Object>} props.events - Array of event objects to populate the form (primary and additional events).
 * @param {Function} [props.onSave] - Callback invoked with form data when the form is submitted. Dispatches are also provided.
 * @param {string} [props.appName] - Name of the app context for the card.
 * @param {string|number} [props.userId] - The user ID of the person editing events (used for tracking or permissions).
 * @param {boolean} [props.saveCardDataToSls] - Whether to save the card data to SLS upon submission.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 *
 * @returns {JSX.Element} The rendered edit-mode form for events.
 */
const EventsCardEditMode = ({
  person,
  reviewed = false,
  setIsEditing,
  events,
  onSave = () => {},
  userId,
  appName,
  saveCardDataToSls,
  onDirtyChange = () => {},
  splitScreenPartitionKey,
}) => {
  const [t] = useTranslation()
  const { types, images, currentImageArk } = useSlsStateContext()
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const slsDispatch = useSlsDispatchContext()
  const { getDispatches, commitEdits, clearEdits } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])
  const defaultValues = useMemo(
    () => parseEventsForForm(events, types, person, images),
    [events, types, person, images]
  )
  // Indexes of the primary events currently expanded for editing. A record can hold more than one
  // primary event, and each can be opened independently — opening one does not collapse the others.
  const [editingPrimaryEventIndexes, setEditingPrimaryEventIndexes] = useState(() => new Set())

  const handleEditPrimaryEvent = useCallback((index) => {
    setEditingPrimaryEventIndexes((prev) => new Set(prev).add(index))
  }, [])

  // The resolver only validates events the user actually modified (FSS-15041), so it needs the
  // current dirty state. dirtyFields comes from useFormState below, which needs the control that
  // useZionForm returns - so it can't be read at this point and a ref bridges the two.
  const dirtyFieldsRef = useRef(null)
  const formContext = useMemo(
    () => ({
      person,
      get dirtyFields() {
        return dirtyFieldsRef.current
      },
    }),
    [person]
  )

  const formMethods = useZionForm({
    defaultValues,
    mode: 'all',
    resolver: eventsResolver,
    context: formContext,
  })

  const primaryEventsArrayMethods = useFieldArray({
    name: 'events.primary',
    control: formMethods.control,
    keyName: 'formId', // to avoid conflict with 'id' from our data
  })
  const additionalEventsArrayMethods = useFieldArray({
    name: 'events.additional',
    control: formMethods.control,
    keyName: 'formId', // to avoid conflict with 'id' from our data
  })

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  const { fields: primaryEvents } = primaryEventsArrayMethods
  const { fields: additionalEvents, append: appendAdditional } = additionalEventsArrayMethods

  // Dynamically generate suggestion buttons from SLS types
  const eventSuggestions = useMemo(() => {
    const suggestions = SUGGESTED_EVENT_TYPES.map((eventTypeKey) => {
      // Find the event type metadata from SLS types
      const eventTypeData = getSlsTypeByValue(eventTypeKey, 'events', types)
      if (!eventTypeData) return null

      return {
        id: eventTypeKey.toLowerCase(),
        label: eventTypeData.displayLabel.toUpperCase(), // e.g., "BIRTH", "MARRIAGE"
        icon: ContentAdd,
        createData: () => createEmptyEventWithType(eventTypeKey, types, person),
        isVisible: () => true, // All button will be visible
        testId: `add-${eventTypeKey.toLowerCase()}-event`,
        linkName: `events-add-${eventTypeKey.toLowerCase()}`,
      }
    }).filter(Boolean) // Remove any null entries if event type not found

    // Add "OTHER EVENT" option that doesn't pre-fill event type
    suggestions.push({
      id: 'other',
      label: t('eventsForm.otherEvent', 'OTHER EVENT'),
      icon: ContentAdd,
      createData: () => createEmptyEventWithType(null, types, person),
      testId: 'add-other-event',
      linkName: 'events-add-other',
    })

    return suggestions
  }, [types, t, person])

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty, dirtyFields, defaultValues: formDefaultValues } = useFormState({ control: formMethods.control })

  // Keep the resolver's view of the dirty state in sync (see formContext above)
  dirtyFieldsRef.current = dirtyFields

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
    clearEdits()
  }, [formMethods, setIsEditing, clearEdits, onDirtyChange])

  // Records an event the user removed this session into the form's events.removed list. The removed
  // event keeps its current linkedPersonIds so save-time deletion (handleDeletedEvents) can reconcile
  // pending panel unlinks against SLS, instead of losing them when the event leaves its live array.
  const handleEventRemoved = useCallback(
    (removedEvent) => {
      if (!removedEvent?.id) return
      const prev = formMethods.getValues('events.removed') || []
      formMethods.setValue('events.removed', [...prev, removedEvent], { shouldDirty: true })
    },
    [formMethods]
  )

  const handleSubmit = useCallback(
    (data) => {
      setError(null)
      setSaving(true)

      // Get highlight dispatches from the highlights context
      const highlightDispatches = getDispatches({
        mainImageArk: currentImageArk,
        splitScreenImageArk: splitScreenPartitionKey,
      })

      const eventsDispatches = getEventsDispatches({
        currentEvents: data.events,
        primaryEventId: formDefaultValues.primaryEventId,
        defaultValues: formDefaultValues?.events,
        images,
        person,
        appName,
        slsTypes: types,
        dirtyFields: dirtyFields?.events,
        highlightDispatches,
      })

      // Combine all dispatches
      const dispatches = [...eventsDispatches]

      if (saveCardDataToSls) {
        const sls = generateSls(dispatches)

        saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          userId,
          dispatch: slsDispatch,
          appName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            setSaving(false)
            onSave(data, dispatches)

            // Only batch dispatches to update local state if saveChangesFlag is off
            if (!saveChangesFlag.isOn) {
              slsDispatch({
                type: 'BATCH_DISPATCHES',
                value: dispatches,
              })
            }
            commitEdits()
          },
          errorHandler: (err) => {
            setError(err)
            setSaving(false)
            errorCollector(err, null, {
              codeId: 90,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          },
          // Processing SLS dispatches to update local state even if the saveChangesFlag is off
        })
      }
      if (!saveCardDataToSls) {
        setSaving(false)
        onSave(data, dispatches)
        commitEdits()
      }
    },
    [
      formDefaultValues.events,
      formDefaultValues.primaryEventId,
      images,
      person,
      saveCardDataToSls,
      saveChangesFlag,
      slsDispatch,
      appName,
      types,
      dirtyFields.events,
      currentImageArk,
      commitEdits,
      getDispatches,
      userId,
      onSave,
      splitScreenPartitionKey,
    ]
  )

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  // Display highlights for all fields in edit mode
  const fieldIds = useMemo(() => getFieldIdsFromFormValues(watchedFormValues), [watchedFormValues])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: !['editSharedHighlightPage1', 'editSharedHighlightPage2', 'highlightsPanel'].includes(topMostPanelId),
  })

  return (
    <ZionForm methods={formMethods} onSubmit={handleSubmit} id="events-card-form">
      <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
        <Cell>
          <H6>{t('eventsForm.primary-event-edit-title', 'Edit Primary Event')}</H6>
          <Paragraph size="sm">
            {t('eventsForm.primary-event-description', 'What is the main event in this document?')}
          </Paragraph>
        </Cell>

        <EventsSection
          events={primaryEvents}
          formArrayMethods={primaryEventsArrayMethods}
          person={person}
          appName={appName}
          userId={userId}
          saveCardDataToSls={saveCardDataToSls}
          splitScreenImageArk={splitScreenPartitionKey}
          onEventRemoved={handleEventRemoved}
          isPrimarySection
          editingPrimaryEventIndexes={editingPrimaryEventIndexes}
          onEditPrimaryEvent={handleEditPrimaryEvent}
        />

        <Cell>
          <H6>{t('eventsForm.additional-events-edit-title', 'Edit Additional Events')}</H6>
          <Paragraph size="sm">
            {t(
              'eventsForm.suggestionButtonsInstruction',
              'What other events do you see in the document about {personName}?',
              { personName: getPersonName({ person, justOneName: true }) || 'this person' }
            )}
          </Paragraph>
        </Cell>

        <EventsSection
          events={additionalEvents}
          formArrayMethods={additionalEventsArrayMethods}
          person={person}
          isPrimarySection={false}
          appName={appName}
          userId={userId}
          saveCardDataToSls={saveCardDataToSls}
          splitScreenImageArk={splitScreenPartitionKey}
          onEventRemoved={handleEventRemoved}
          editingPrimaryEventIndexes={editingPrimaryEventIndexes}
          onEditPrimaryEvent={handleEditPrimaryEvent}
        />

        <SuggestionButtons
          suggestions={eventSuggestions}
          onSuggestionClick={(data) => {
            formMethods.trigger()
            appendAdditional(data)
          }}
        />

        {error && (
          <Alert type="error" outlined dense>
            {t('events-card.save-error-msg', 'Error saving data. Please try again later.')}
          </Alert>
        )}

        <CardFooter
          reviewed={reviewed}
          hasData={
            (watchedFormValues.events.additional.length > 0 &&
              watchedFormValues.events.additional.some((field) => field.eventType)) ||
            watchedFormValues.events.primary.length > 0
          }
          onCancel={handleCancel}
          saving={saving}
          label="events"
        />
      </FlowGrid>
    </ZionForm>
  )
}
EventsCardEditMode.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string,
    recordId: PropTypes.string.isRequired,
    primary: PropTypes.bool,
  }),
  reviewed: PropTypes.bool,
  setIsEditing: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  appName: PropTypes.string,
  userId: PropTypes.string.isRequired,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
}

EventsCardEditMode.defaultProps = {
  person: null,
  reviewed: false,
  setIsEditing: () => {},
  events: [],
  appName: 'FullTextApp',
}

export default EventsCardEditMode
